import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, Send, StickyNote, ArrowRightLeft, CheckCircle2, Zap,
  MessageSquare, Phone, Circle, Loader2, MoreVertical, Paperclip, Sparkles,
  FileText, Receipt, DollarSign, Files, Bot, RotateCcw, Clock,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { AttachmentView } from "@/components/AttachmentView";
import { ChatAttachments } from "@/components/ChatAttachments";
import { TaskDialog } from "@/components/TaskDialog";
import { ChatOpportunities } from "@/components/ChatOpportunities";

const STATUS_TABS = [
  { value: "open", label: "Abertas" },
  { value: "pending", label: "Aguardando" },
  { value: "resolved", label: "Finalizadas" },
  { value: "all", label: "Todas" },
];

const addUnique = (list, m) => (list.some((x) => x.id === m.id) ? list.map((x) => (x.id === m.id ? { ...x, ...m } : x)) : [...list, m]);


function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export default function InboxPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [status, setStatus] = useState("open");
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState("");
  const [text, setText] = useState("");
  const [isNote, setIsNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [users, setUsers] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferDept, setTransferDept] = useState("");
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState("all");
  const [transfers, setTransfers] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [attachmentsKey, setAttachmentsKey] = useState(0);
  const [contactMeta, setContactMeta] = useState(null);
  const [payments, setPayments] = useState([]);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfForm, setPdfForm] = useState({
    kind: "Cobrança", razao_social: "", cnpj: "", codigo_receita: "",
    periodo_apuracao: "", vencimento: "", valor: "", numero_documento: "", observacoes: "",
  });
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const messagesRef = useRef(null);
  const [filesOpen, setFilesOpen] = useState(false);
  const [taskInit, setTaskInit] = useState(null);

  const loadChats = async () => {
    try {
      const { data } = await api.get("/chats", { params: { status, mine, department: deptFilter, q: search || undefined } });
      setChats(data);
    } catch (_) {}
  };

  useEffect(() => {
    const t = setTimeout(loadChats, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [status, mine, deptFilter, search]);

  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepartments(data.filter((d) => d.active))).catch(() => {});
    api.get("/users/directory").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!chatId) { setTransfers([]); return; }
    api.post(`/chats/${chatId}/read`).then(loadChats).catch(() => {});
    api.get(`/chats/${chatId}/transfers`).then(({ data }) => setTransfers(data)).catch(() => {});
  }, [chatId, activeChat?.assignee_id, activeChat?.department]);

  useEffect(() => {
    api.get("/quick-replies").then(({ data }) => setQuickReplies(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!chatId) { setActiveChat(null); setMessages([]); setContactMeta(null); setPayments([]); return; }
    api.get(`/chats/${chatId}`).then(({ data }) => {
      setActiveChat(data);
      setStatus((cur) => (cur === "all" || cur === data.status ? cur : data.status));
      api.get("/contacts").then(({ data: cs }) => {
        const c = cs.find(x => x.phone === data.phone);
        setContactMeta(c || null);
        setPdfForm(f => ({ ...f, razao_social: c?.name || data.contact_name || "", cnpj: c?.cnpj || "" }));
      }).catch(() => {});
      api.get(`/payments/${data.phone}`).then(({ data }) => setPayments(data)).catch(() => setPayments([]));
    });
    api.get(`/chats/${chatId}/messages`).then(({ data }) => setMessages(data));
  }, [chatId]);

  useEffect(() => {
    setTimeout(() => { const el = messagesRef.current; if (el) el.scrollTop = el.scrollHeight; }, 40);
  }, [messages]);

  useLiveEvents((payload) => {
    if (payload.event === "message") {
      if (payload.data.chat_id === chatId) {
        setMessages((prev) => addUnique(prev, payload.data));
      }
      loadChats();
    }
    if (payload.event === "chat_updated") {
      loadChats();
      if (payload.data.id === chatId) setActiveChat(payload.data);
    }
    if (payload.event === "chats_merged") {
      loadChats();
      if (payload.data.removed.includes(chatId)) navigate(`/inbox/${payload.data.into}`, { replace: true });
      else if (payload.data.into === chatId) api.get(`/chats/${chatId}/messages`).then(({ data }) => setMessages(data));
    }
    if (payload.event === "attachment_updated" && payload.data.chat_id === chatId) {
      const { message_id, media_status, media_name, media_mime } = payload.data;
      setMessages((prev) => prev.map((m) => (m.message_id === message_id
        ? { ...m, media_status, ...(media_name ? { media_name } : {}), ...(media_mime ? { media_mime } : {}) } : m)));
      setAttachmentsKey((k) => k + 1);
    }
    if (payload.event === "message" && payload.data.chat_id === chatId && payload.data.media_type) {
      setAttachmentsKey((k) => k + 1);
    }
  });

  const filteredChats = chats;

  const send = async () => {
    if (!text.trim() || !activeChat) return;
    setSending(true);
    try {
      if (isNote) {
        const { data } = await api.post(`/chats/${activeChat.id}/note`, { text });
        setMessages((prev) => addUnique(prev, data));
      } else {
        const { data } = await api.post(`/chats/${activeChat.id}/send`, { text });
        setMessages((prev) => addUnique(prev, data));
      }
      setText("");
      loadChats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSending(false);
    }
  };

  const resolve = async () => {
    if (!activeChat) return;
    try {
      await api.patch(`/chats/${activeChat.id}`, { status: "resolved" });
      toast.success("Atendimento finalizado");
      loadChats();
      navigate("/inbox");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao finalizar"); }
  };

  const reopen = async () => {
    if (!activeChat) return;
    try {
      const { data } = await api.post(`/chats/${activeChat.id}/reopen`);
      setActiveChat(data);
      toast.success("Atendimento reaberto (sem responsável)");
      loadChats();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao reabrir"); }
  };

  const setPriority = async (priority) => {
    try {
      const { data } = await api.patch(`/chats/${activeChat.id}`, { priority });
      setActiveChat(data);
      loadChats();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao alterar prioridade"); }
  };

  const doTransfer = async () => {
    if (!activeChat) return;
    if (!transferTo && !transferDept) { toast.error("Escolha um setor ou atendente"); return; }
    try {
      const { data } = await api.post(`/chats/${activeChat.id}/transfer`, {
        assignee_id: transferTo || null,
        department: transferDept || null,
        reason: transferReason,
      });
      setActiveChat(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na transferência");
      return;
    }
    setTransferOpen(false);
    setTransferReason("");
    setTransferTo("");
    setTransferDept("");
    toast.success("Atendimento transferido");
  };

  const assignToMe = async () => {
    if (!activeChat) return;
    try {
      await api.patch(`/chats/${activeChat.id}`, { assignee_id: user.id, status: "open" });
      toast.success("Você assumiu este atendimento");
      loadChats();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível assumir o atendimento"); }
  };

  const applyQuickReply = (qr) => { setText((t) => (t ? `${t} ${qr.text}` : qr.text)); };

  const suggestAI = async () => {
    if (!activeChat) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/chats/${activeChat.id}/suggest`, { hint: text || null });
      if (data.suggestion) {
        setText(data.suggestion);
        toast.success("Sugestão da IA aplicada — revise antes de enviar");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na sugestão");
    } finally {
      setSuggesting(false);
    }
  };

  const sendInvoicePdf = async () => {
    if (!activeChat) return;
    const payload = {
      ...pdfForm,
      valor: parseFloat(String(pdfForm.valor).replace(",", ".")) || 0,
    };
    if (!payload.razao_social || !payload.cnpj || !payload.vencimento || !payload.valor) {
      toast.error("Preencha razão social, CNPJ, vencimento e valor"); return;
    }
    setSending(true);
    try {
      await api.post(`/chats/${activeChat.id}/invoice-pdf`, payload);
      toast.success(`${pdfForm.kind} enviado em PDF`);
      setPdfOpen(false);
      api.get(`/payments/${activeChat.phone}`).then(({ data }) => setPayments(data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao gerar PDF");
    } finally { setSending(false); }
  };

  const sendFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !activeChat) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("caption", text || "");
    setSending(true);
    try {
      await api.post(`/chats/${activeChat.id}/send-media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText("");
      toast.success(`Enviado: ${f.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar arquivo");
    } finally { setSending(false); }
  };

  return (
    <div className="h-full grid grid-cols-1 md:grid-cols-12 grid-rows-[minmax(0,1fr)] overflow-hidden">
      {/* Left: chats list */}
      <div className={`md:col-span-4 lg:col-span-3 border-r border-border bg-card flex flex-col h-full ${chatId ? "hidden md:flex" : "flex"}`}>
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="heading font-bold text-lg">Atendimentos</h2>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate("/contacts")} data-testid="inbox-new-chat-button" title="Nova conversa">
                <MessageSquare className="w-4 h-4 mr-1" /> Nova
              </Button>
              <Badge variant="secondary">{filteredChats.length}</Badge>
            </div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="chat-search-input"
              placeholder="Buscar nome, telefone, CPF ou CNPJ"
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={mine ? "default" : "outline"} size="sm"
              onClick={() => setMine((m) => !m)} data-testid="chat-filter-mine"
              className={mine ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              {mine ? "Minhas" : "Todas"}
            </Button>
            <Tabs value={status} onValueChange={setStatus} className="flex-1">
              <TabsList data-testid="chat-filter-status" className="w-full h-9 grid grid-cols-4">
                {STATUS_TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value} className="text-xs">{t.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="h-9 text-xs" data-testid="chat-filter-department"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os setores</SelectItem>
              <SelectItem value="none">Sem setor</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <ScrollArea className="flex-1 min-w-0 [&>[data-radix-scroll-area-viewport]>div]:!block">
          {filteredChats.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Nenhuma conversa nesta caixa.
            </div>
          ) : filteredChats.map((c) => (
            <button
              key={c.id}
              data-testid="chat-conversation-item"
              onClick={() => navigate(`/inbox/${c.id}`)}
              className={`w-full max-w-full min-w-0 overflow-hidden text-left px-4 py-3 border-b border-border/60 hover:bg-muted/60 transition-colors flex gap-3 items-start ${
                c.id === chatId ? "bg-emerald-50/70 dark:bg-emerald-950/30" : ""
              }`}
            >
              <Avatar className="w-11 h-11 flex-shrink-0">
                <AvatarFallback className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 text-sm font-semibold">
                  {(c.contact_name || c.phone || "?").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold truncate text-sm flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{c.contact_name || c.phone}</span>
                    {c.transfer_alert_for === user?.id && (
                      <span className="relative flex w-4 h-4 shrink-0" title="Transferido para você" data-testid="chat-transfer-alert">
                        <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-75" />
                        <ArrowRightLeft className="relative w-4 h-4 p-0.5 rounded-full bg-amber-500 text-white" />
                      </span>
                    )}
                    {c.wait_alerted && c.status !== "resolved" && (
                      <span className="relative flex w-4 h-4 shrink-0" title="Aguardando resposta há muito tempo" data-testid="chat-wait-alert">
                        <span className="absolute inset-0 rounded-full bg-orange-400 animate-ping opacity-75" />
                        <Clock className="relative w-4 h-4 p-0.5 rounded-full bg-orange-500 text-white" />
                      </span>
                    )}
                    {!c.assignee_id && c.status !== "resolved" && (
                      <span className="relative flex w-2.5 h-2.5 shrink-0" title="Sem atendimento" data-testid="chat-unattended-alert">
                        <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-75" />
                        <span className="relative w-2.5 h-2.5 rounded-full bg-rose-600" />
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground shrink-0">{fmtTime(c.last_message_at)}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message || "—"}</div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {c.status === "pending" && <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 text-[10px] px-1.5">Aguardando</Badge>}
                  {c.status === "open" && <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200 text-[10px] px-1.5">Aberto</Badge>}
                  {c.status === "resolved" && <Badge variant="secondary" className="text-[10px] px-1.5">Finalizado</Badge>}
                  {["alta", "urgente"].includes(c.priority) && <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200 text-[10px] px-1.5">{c.priority === "urgente" ? "Urgente" : "Alta"}</Badge>}
                  {c.bot_active && c.status !== "resolved" && <Badge className="bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200 text-[10px] px-1.5">IA</Badge>}
                  {c.department && <span className="text-[10px] text-muted-foreground truncate">{c.department}</span>}
                  {c.assignee_name && <span className="text-[10px] text-muted-foreground truncate">· {c.assignee_name}</span>}
                  {c.unread > 0 && <Badge className="bg-emerald-600 text-white text-[10px] ml-auto">{c.unread}</Badge>}
                </div>
              </div>
            </button>
          ))}
        </ScrollArea>
      </div>

      {/* Middle: active chat */}
      <div className={`md:col-span-8 lg:col-span-6 flex flex-col h-full chat-canvas ${chatId ? "flex" : "hidden md:flex"}`}>
        {!activeChat ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-emerald-700 dark:text-emerald-300" />
            </div>
            <h3 className="heading text-xl font-bold mb-2">Selecione uma conversa</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Escolha um atendimento na lista à esquerda para visualizar e responder.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div data-testid="chat-active-header" className="h-16 border-b border-border bg-card flex items-center px-2 sm:px-4 gap-1.5 sm:gap-3 min-w-0 overflow-hidden">
              <Button variant="ghost" size="sm" className="md:hidden px-2" onClick={() => navigate("/inbox")}>←</Button>
              <Avatar className="w-9 h-9 sm:w-10 sm:h-10 shrink-0">
                <AvatarFallback className="bg-emerald-600 text-white text-sm">
                  {(activeChat.contact_name || activeChat.phone).slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">
                  <span className="truncate">{activeChat.contact_name}</span>
                  {activeChat.bot_active && activeChat.status !== "resolved" && (
                    <Badge className="bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200 text-[10px] shrink-0" data-testid="chat-bot-badge">
                      <Bot className="w-3 h-3 mr-1" /> IA atendendo
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 min-w-0">
                  <Phone className="w-3 h-3 shrink-0" /> <span className="truncate">{activeChat.phone}</span>
                  {activeChat.assignee_name && <span className="truncate hidden sm:inline">· {activeChat.assignee_name}</span>}
                </div>
              </div>
              <Button size="sm" variant="outline" className="lg:hidden px-2" onClick={() => setFilesOpen(true)} data-testid="chat-files-mobile-button" title="Arquivos da conversa">
                <Files className="w-4 h-4" />
              </Button>
              {(!activeChat.assignee_id || activeChat.assignee_id !== user.id) && (
                <Button size="sm" variant="outline" className="px-2 sm:px-3" onClick={assignToMe} data-testid="chat-assign-me-button">Assumir</Button>
              )}
              <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="px-2 sm:px-3" data-testid="chat-transfer-button" title="Transferir">
                    <ArrowRightLeft className="w-4 h-4 xl:mr-1" /> <span className="hidden xl:inline">Transferir</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Transferir atendimento</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold uppercase text-muted-foreground">Para setor</label>
                      <Select value={transferDept} onValueChange={(v) => { setTransferDept(v); setTransferTo(""); }}>
                        <SelectTrigger className="mt-1" data-testid="transfer-department-select"><SelectValue placeholder="Selecione o setor..." /></SelectTrigger>
                        <SelectContent>
                          {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase text-muted-foreground">Para atendente (opcional)</label>
                      <Select value={transferTo} onValueChange={setTransferTo}>
                        <SelectTrigger className="mt-1" data-testid="transfer-user-select"><SelectValue placeholder={transferDept ? "Qualquer um do setor" : "Selecione..."} /></SelectTrigger>
                        <SelectContent>
                          {users.filter((u) => !transferDept || u.department === transferDept).map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name}{u.department ? ` · ${u.department}` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase text-muted-foreground">Motivo (opcional)</label>
                      <Textarea value={transferReason} onChange={(e) => setTransferReason(e.target.value)}
                                placeholder="Ex.: cliente pediu orçamento" className="mt-1" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={doTransfer} className="bg-emerald-600 hover:bg-emerald-700" data-testid="transfer-confirm-button">Confirmar</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <ChatOpportunities key={`opp-btn-${activeChat.id}`} chat={activeChat} compact />
              <Button size="sm" variant="outline" className="px-2 sm:px-3" onClick={() => toast.info("Em breve", { description: "A geração de documentos em PDF pelo chat estará disponível em breve." })} data-testid="chat-invoice-button" title="Gerar documento em PDF (em breve)">
                <Receipt className="w-4 h-4 xl:mr-1" /> <span className="hidden xl:inline">PDF</span>
              </Button>
              {activeChat.status === "resolved" ? (
                <Button size="sm" variant="outline" className="px-2 sm:px-3" onClick={reopen} data-testid="chat-reopen-button" title="Reabrir">
                  <RotateCcw className="w-4 h-4 xl:mr-1" /> <span className="hidden xl:inline">Reabrir</span>
                </Button>
              ) : (
              <Button size="sm" variant="outline" className="px-2 sm:px-3" onClick={resolve} data-testid="chat-finish-button" title="Finalizar">
                <CheckCircle2 className="w-4 h-4 xl:mr-1" /> <span className="hidden xl:inline">Finalizar</span>
              </Button>
              )}
            </div>
            <TaskDialog open={!!taskInit} onOpenChange={(v) => { if (!v) setTaskInit(null); }} initial={taskInit} />
            <Sheet open={filesOpen} onOpenChange={setFilesOpen}>
              <SheetContent side="right" className="w-[92vw] sm:max-w-md overflow-y-auto">
                <SheetHeader><SheetTitle>Arquivos da conversa</SheetTitle></SheetHeader>
                <div className="mt-4"><ChatAttachments chatId={activeChat.id} refreshKey={attachmentsKey} /></div>
              </SheetContent>
            </Sheet>

            {/* Messages */}
            <div ref={messagesRef} data-testid="chat-messages-container" className="flex-1 min-h-0 overflow-y-auto px-4 py-6 space-y-3">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.from_me ? "justify-end" : "justify-start"}`}>
                  <div className={m.is_note ? "bubble-note" : (m.from_me ? "bubble-sent" : "bubble-received")}>
                    {m.is_note && (
                      <div className="text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                        <StickyNote className="w-3 h-3" /> Nota interna · {m.sender_name}
                      </div>
                    )}
                    {m.is_bot && (
                      <div className="text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1 opacity-90" data-testid="bot-message-label">
                        <Bot className="w-3 h-3" /> {m.sender_name}
                      </div>
                    )}
                    {m.from_me && !m.is_bot && !m.is_note && m.sender_name && (
                      <div className="text-[10px] font-semibold mb-0.5 opacity-80 flex items-center gap-1.5" data-testid="message-sender">
                        <UserAvatar user={users.find((u) => u.id === m.sender_user_id) || { name: m.sender_name }}
                                    className="w-5 h-5" fallbackClass="bg-emerald-800 text-white text-[8px]" />
                        {m.sender_name}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words text-sm">
                      {m.media_type && <AttachmentView message={m} />}
                      {m.media_type && m.body ? <br /> : null}
                      {m.body}
                    </div>
                    <div className={`text-[10px] mt-1 flex items-center gap-2 ${m.from_me && !m.is_note ? "text-emerald-100" : "text-muted-foreground"}`}>
                      {fmtTime(m.timestamp)}
                      {!m.is_note && (
                        <button onClick={() => setTaskInit({ title: (m.body || m.media_name || "Tarefa").slice(0, 80), description: m.body || "", chat_id: m.chat_id, message_id: m.message_id || m.id })}
                                className="underline opacity-80 hover:opacity-100" data-testid="message-create-task-button" title="Criar tarefa a partir desta mensagem">
                          + tarefa
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Composer */}
            <div className="border-t border-border bg-card p-3 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {quickReplies.slice(0, 6).map((qr) => (
                  <button
                    key={qr.id}
                    data-testid="chat-quick-reply-button"
                    onClick={() => applyQuickReply(qr)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-200 border border-emerald-200/60 dark:border-emerald-800/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/60"
                  >
                    <Zap className="w-3 h-3" /> /{qr.shortcut}
                  </button>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <input ref={fileInputRef} type="file" className="hidden" onChange={sendFile}
                       accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.xml,.txt" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                        data-testid="chat-attach-button" disabled={sending} title="Anexar arquivo">
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Button
                  variant={isNote ? "default" : "outline"} size="sm"
                  onClick={() => setIsNote((n) => !n)}
                  data-testid="chat-internal-note-toggle"
                  className={isNote ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                >
                  <StickyNote className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={suggestAI} disabled={suggesting}
                        data-testid="chat-ai-suggest-button" title="Sugerir resposta com IA">
                  {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-emerald-600" />}
                </Button>
                <Textarea
                  data-testid="chat-input-textarea"
                  value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={isNote ? "Nota interna (privada da equipe)" : "Digite sua mensagem..."}
                  rows={2}
                  className={`resize-none ${isNote ? "bg-amber-50 dark:bg-amber-950/30 border-amber-300" : ""}`}
                />
                <Button
                  data-testid="chat-send-button"
                  onClick={send} disabled={sending || !text.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 h-11 px-4"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right: contact panel */}
      <div className="hidden lg:flex lg:col-span-3 border-l border-border bg-card flex-col h-full overflow-y-auto">
        {activeChat ? (
          <div className="p-5 space-y-5">
            <div className="text-center">
              <Avatar className="w-20 h-20 mx-auto mb-3">
                <AvatarFallback className="bg-emerald-600 text-white text-xl">
                  {(activeChat.contact_name || activeChat.phone).slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="heading font-bold text-lg">{activeChat.contact_name}</div>
              <div className="text-sm text-muted-foreground mono">+{activeChat.phone}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <InfoRow label="CPF/CNPJ" value={contactMeta?.cnpj || "—"} />
              <InfoRow label="Setor" value={activeChat.department || "—"} />
              <InfoRow label="Status" value={
                <Badge className={
                  activeChat.status === "open" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" :
                  activeChat.status === "pending" ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" :
                  "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                }>{{ open: "Aberto", pending: "Aguardando", resolved: "Finalizado" }[activeChat.status] || activeChat.status}</Badge>
              } />
              <InfoRow label="Atendente" value={activeChat.assignee_name || "—"} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(contactMeta?.tags || []).map((t) => (
                <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
              ))}
            </div>
            <ChatOpportunities key={`opp-panel-${activeChat.id}`} chat={activeChat} />
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <DollarSign className="w-3 h-3" /> Histórico de Pagamentos
              </div>
              {payments.length === 0 ? (
                <div className="text-xs text-muted-foreground">Sem registros ainda.</div>
              ) : (
                <div className="space-y-2">
                  {payments.slice(0, 8).map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted/40 border border-border/60">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold truncate">{p.kind} · {p.reference || "—"}</div>
                        <div className="text-[10px] text-muted-foreground">Venc. {p.due_date}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-bold mono">R$ {Number(p.amount).toFixed(2).replace(".", ",")}</div>
                        <Badge className={`text-[9px] px-1.5 ${
                          p.status === "paid" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" :
                          p.status === "overdue" ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" :
                          "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
                        }`}>{{ pending: "Pendente", paid: "Pago", overdue: "Vencido" }[p.status] || p.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <InfoRow label="Setor" value={activeChat.department || "—"} />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Prioridade</div>
                <Select value={activeChat.priority || "normal"} onValueChange={setPriority}>
                  <SelectTrigger className="h-8 mt-1 text-xs" data-testid="chat-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {activeChat.subject && <div className="col-span-2"><InfoRow label="Assunto (IA)" value={activeChat.subject} /></div>}
            </div>
            {transfers.length > 0 && (
              <div data-testid="chat-transfer-history">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <ArrowRightLeft className="w-3 h-3" /> Histórico de transferências
                </div>
                <div className="space-y-1.5">
                  {transfers.slice(0, 6).map((t) => (
                    <div key={t.id} className="text-[11px] p-2 rounded-lg bg-muted/40 border border-border/60">
                      <div className="font-semibold">{t.from_department || "—"} → {t.to_department || "—"}{t.to_user ? ` · ${t.to_user}` : ""}</div>
                      <div className="text-muted-foreground">{t.by} · {new Date(t.created_at).toLocaleString("pt-BR")}{t.reason ? ` · ${t.reason}` : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <ChatAttachments chatId={activeChat.id} refreshKey={attachmentsKey} />
          </div>
        ) : (
          <div className="p-6 text-sm text-muted-foreground text-center">
            Selecione uma conversa para ver os detalhes do contato.
          </div>
        )}
      </div>

      {/* Invoice PDF Dialog */}
      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-600" /> Gerar documento em PDF</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-4 gap-2">
              {["Cobrança", "Boleto", "Orçamento", "Recibo"].map(k => (
                <button key={k} type="button" onClick={() => setPdfForm(f => ({ ...f, kind: k }))}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                          pdfForm.kind === k ? "bg-emerald-600 text-white border-emerald-600" : "bg-transparent border-border hover:bg-muted"
                        }`}>{k}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs">Nome / Razão Social</label><Input value={pdfForm.razao_social} onChange={(e) => setPdfForm({ ...pdfForm, razao_social: e.target.value })} /></div>
              <div><label className="text-xs">CNPJ/CPF</label><Input value={pdfForm.cnpj} onChange={(e) => setPdfForm({ ...pdfForm, cnpj: e.target.value })} /></div>
              <div><label className="text-xs">Código / Referência</label><Input value={pdfForm.codigo_receita} onChange={(e) => setPdfForm({ ...pdfForm, codigo_receita: e.target.value })} placeholder="Opcional" /></div>
              <div><label className="text-xs">Período / Competência</label><Input value={pdfForm.periodo_apuracao} onChange={(e) => setPdfForm({ ...pdfForm, periodo_apuracao: e.target.value })} placeholder="05/2026" /></div>
              <div><label className="text-xs">Vencimento</label><Input type="date" value={pdfForm.vencimento} onChange={(e) => setPdfForm({ ...pdfForm, vencimento: e.target.value })} /></div>
              <div><label className="text-xs">Valor (R$)</label><Input value={pdfForm.valor} onChange={(e) => setPdfForm({ ...pdfForm, valor: e.target.value })} placeholder="0,00" /></div>
              <div className="col-span-2"><label className="text-xs">Nº do Documento</label><Input value={pdfForm.numero_documento} onChange={(e) => setPdfForm({ ...pdfForm, numero_documento: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs">Observações</label><Textarea rows={2} value={pdfForm.observacoes} onChange={(e) => setPdfForm({ ...pdfForm, observacoes: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendInvoicePdf} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Gerar e enviar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}
